
import { create } from 'zustand';

// UI-only mode - NEVER grants permissions, only affects UI display
export type UIMode = 'admin' | 'staff';

interface UserModeState {
  currentMode: UIMode;
  setMode: (mode: UIMode) => void;
  initializeModeForUser: (serverRole?: string) => void;
}

export const useUserMode = create<UserModeState>((set, get) => ({
  currentMode: 'staff', // Always default to staff for security
  
  setMode: (mode: UIMode) => {
    try {
      localStorage.setItem('user-mode', mode);
      set({ currentMode: mode });
    } catch (error) {
      console.warn('Failed to save user mode to localStorage:', error);
      set({ currentMode: mode });
    }
  },

  initializeModeForUser: (serverRole?: string) => {
    try {
      const savedMode = localStorage.getItem('user-mode') as UIMode;
      
      // Only allow admin UI mode if server role is admin/manager
      if (savedMode === 'admin' && (serverRole === 'admin' || serverRole === 'manager')) {
        set({ currentMode: 'admin' });
      } else if (savedMode === 'staff') {
        set({ currentMode: 'staff' });
      } else {
        // Default based on server role
        const defaultMode = (serverRole === 'admin' || serverRole === 'manager') ? 'admin' : 'staff';
        set({ currentMode: defaultMode });
        localStorage.setItem('user-mode', defaultMode);
      }
    } catch (error) {
      console.warn('Failed to initialize mode from localStorage:', error);
      set({ currentMode: 'staff' });
    }
  }
}));

// Initialize from localStorage with proper error handling and role-based restrictions
if (typeof window !== 'undefined') {
  try {
    const savedMode = localStorage.getItem('user-mode') as UIMode;
    // Only apply saved mode if it's valid - role-based restrictions will be applied in initializeModeForUser
    if (savedMode && (savedMode === 'admin' || savedMode === 'staff')) {
      useUserMode.setState({ currentMode: savedMode });
    }
  } catch (error) {
    console.warn('Failed to read user mode from localStorage:', error);
  }
}
